import type { Uuid } from "spacetimedb";
import { useMutation } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { useSpacetimeDB } from "spacetimedb/tanstack";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Label } from "@/web/components/primitives/label";
import { Spinner } from "@/web/components/primitives/spinner";
import { toaster } from "@/web/components/primitives/toast";
import { ToggleGroup, ToggleGroupItem } from "@/web/components/primitives/toggle-group";
import { ErrorHandler } from "@/web/lib/errors";
import type { DbConnection } from "@tycho/web-bindings";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";
import { usePostHog } from "@posthog/react";
import { PosthogEvents } from "@/web/lib/posthog-events";

const PDF_CONTENT_TYPE = "application/pdf";

type UploadTaxReturnProps = {
  /** Underwritable entity that will own the resulting tax return row. */
  underwritableEntity: Uuid;
};

function UploadTaxReturnAction({
  underwritableEntity,
  onSkip,
  onComplete,
}: UploadTaxReturnProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ActionCard category="budget">
        <ActionCard.Header>
          <ActionCard.Badge>Fundraise</ActionCard.Badge>
          <ActionCard.DetailsTrigger />
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>Upload tax return</ActionCard.Title>
          <ActionCard.Description>
            Share your latest business tax return so we can verify revenue and underwrite your deal
            faster.
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>Upload</ActionCard.PrimaryButton>
        </ActionCard.Footer>
        <ActionCard.Back title="Why we ask">
          <p>
            Tax returns are the most reliable signal of business revenue and expenses. Sharing one
            cuts back-and-forth during underwriting and helps us match you with the right financing.
            Files are uploaded directly to encrypted storage — bytes never sit on our servers.
          </p>
        </ActionCard.Back>
      </ActionCard>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Upload tax return</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          {/*
            vaul portals DrawerContent on open/close, so this body remounts
            each session — no need to manually reset taxYear / file state.
          */}
          <UploadTaxReturnDrawerBody
            underwritableEntity={underwritableEntity}
            onComplete={() => {
              setOpen(false);
              onComplete();
            }}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}

function UploadTaxReturnDrawerBody({
  underwritableEntity,
  onComplete,
}: {
  underwritableEntity: Uuid;
  onComplete: () => void;
}) {
  const currentYear = new Date().getFullYear();
  const allowedTaxYears = [
    currentYear - 1,
    currentYear - 2,
    currentYear - 3,
    currentYear - 4,
  ] as const;
  const [taxYear, setTaxYear] = useState<number>(allowedTaxYears[0]);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const taxYearLabelId = useId();
  const fileInputId = useId();
  const posthog = usePostHog();

  // oxlint-disable-next-line unbound-method - spacetime booboo
  const { getConnection } = useSpacetimeDB();

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Please choose a tax return PDF.");
      if (!allowedTaxYears.includes(taxYear)) {
        throw new Error("Please choose a valid tax year.");
      }
      const connection = getConnection() as DbConnection | null;
      if (!connection) throw new Error("Not connected to server — please try again.");

      const { uploadUrl, objectPath, contentType, contentLengthRange } =
        await connection.procedures.createTaxReturnUploadUrl({
          underwritableEntity,
          contentType: file.type || PDF_CONTENT_TYPE,
        });

      // Headers MUST match the signed URL exactly — same constraint as the
      // hero image flow in CustomizingStyle.tsx:555-573.
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "content-type": contentType,
          "x-goog-content-length-range": contentLengthRange,
        },
        body: file,
      });
      if (!putRes.ok) {
        const text = await putRes.text().catch((e: unknown) => {
          ErrorHandler.reportError(e);
          return "";
        });
        throw new Error(`Upload to GCS failed (${putRes.status}): ${text}`);
      }

      await connection.procedures.submitTaxReturn({
        underwritableEntity,
        objectPath,
        taxYear,
        originalFileName: file.name,
      });
    },
    onSuccess: () => {
      toaster({ message: "Tax return uploaded — we'll parse it shortly.", variant: "success" });
      PosthogEvents.TaxReturnUploaded(posthog, underwritableEntity);
      onComplete();
    },
    onError: ErrorHandler.default,
  });

  const isPending = upload.isPending;
  const canSubmit = file != null && !isPending;

  return (
    <>
      <DrawerBody className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label id={taxYearLabelId} className="text-sm font-medium">
            Tax year
          </Label>
          <ToggleGroup
            type="single"
            aria-labelledby={taxYearLabelId}
            value={String(taxYear)}
            onValueChange={(v) => {
              if (v === "") return;
              const y = Number(v);
              if (allowedTaxYears.includes(y)) setTaxYear(y);
            }}
            disabled={isPending}
            variant="outline"
            spacing={0}
            color="blue"
            className="w-full"
          >
            {allowedTaxYears.map((year) => (
              <ToggleGroupItem key={year} value={String(year)} className="flex-1">
                {year}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={fileInputId}>Tax return (PDF)</Label>
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            accept={PDF_CONTENT_TYPE}
            className="sr-only"
            disabled={isPending}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <Button
            type="button"
            variant="outline"
            color="blue"
            size="md"
            disabled={isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? file.name : "Choose PDF"}
          </Button>
        </div>
        {isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner size="medium" />
            <span>Uploading…</span>
          </div>
        ) : null}
      </DrawerBody>
      <DrawerFooter>
        <Button color="blue" size="lg" disabled={!canSubmit} onClick={() => upload.mutate()}>
          {isPending ? "Uploading…" : "Upload"}
        </Button>
      </DrawerFooter>
    </>
  );
}

export function createUploadTaxReturnAction(
  props: UploadTaxReturnProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "upload-tax-return", canSkip = true, ...rest } = props;
  return {
    id,
    category: "budget",
    canSkip,
    render: (core) => <UploadTaxReturnAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import type { TaxReturnConnection } from "@tycho/web-bindings/types";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createUploadTaxReturnCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/UploadTaxReturnCompleted";

export const uploadTaxReturnRule = CardRulesEngine.singletonRule({
  id: "connect-tax-return",
  priority: 50,
  when: CardRulesEngine.and(
    CardRulesEngine.missingConnection("taxReturns"),
    CardRulesEngine.not(CardRulesEngine.hasNoScheduledCalls),
  ),
  build: (d) => createUploadTaxReturnAction({ underwritableEntity: d.underwritableEntityId }),
});

export const uploadTaxReturnCompletedRule =
  CardRulesEngine.forEachCompletedRule<TaxReturnConnection>({
    id: "connect-tax-return:done",
    forEach: CardRulesEngine.eachConnection("taxReturns"),
    when: CardRulesEngine.always,
    build: (_, connection) => createUploadTaxReturnCompleted({ connection }),
  });
